/** Helpers to turn Manapool bulk-price preview payloads into operator summaries. */

export interface BulkPricePreviewItem {
  inventoryId?: string;
  cardName?: string;
  itemName?: string;
  setCode?: string;
  collectorNumber?: string;
  conditionId?: string;
  finishId?: string;
  languageId?: string;
  beforePriceCents: number;
  afterPriceCents: number;
  changeCents: number;
  marketLowCents?: number | null;
  marketPriceCents?: number | null;
  status?: string;
  skipReason?: string;
  anomalyType?: string;
  anomalyRatio?: number | null;
  confidence?: number | null;
}

export interface BulkPricePreviewSummary {
  total: number;
  success: number;
  skipped: number;
  increases: number;
  decreases: number;
  unchanged: number;
  totalIncreaseCents: number;
  totalDecreaseCents: number;
  avgAbsChangeCents: number;
  maxIncrease: BulkPricePreviewItem | null;
  maxDecrease: BulkPricePreviewItem | null;
  anomalyCount: number;
  items: BulkPricePreviewItem[];
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return undefined;
}

/** Manapool preview prices are integer cents. */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

export function formatSignedCents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const sign = cents > 0 ? '+' : cents < 0 ? '−' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function rowLooksLikePreviewItem(row: unknown): boolean {
  if (!row || typeof row !== 'object') return false;
  const r = row as Record<string, unknown>;
  const before =
    asNumber(r.beforePrice) ??
    asNumber(r.before_price) ??
    asNumber(r.currentPrice) ??
    asNumber(r.current);
  const after =
    asNumber(r.afterPrice) ??
    asNumber(r.after_price) ??
    asNumber(r.newPrice) ??
    asNumber(r.new);
  return before != null && after != null;
}

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

/** Find the largest array of objects that look like Manapool preview rows. */
function findPreviewRowArrays(payload: unknown, depth = 0): unknown[][] {
  if (depth > 6 || payload == null) return [];
  const parsed = parseMaybeJson(payload);
  if (Array.isArray(parsed)) {
    if (parsed.length > 0 && parsed.every(rowLooksLikePreviewItem)) {
      return [parsed];
    }
    const nested: unknown[][] = [];
    for (const entry of parsed.slice(0, 20)) {
      nested.push(...findPreviewRowArrays(entry, depth + 1));
    }
    return nested;
  }
  if (parsed && typeof parsed === 'object') {
    const nested: unknown[][] = [];
    for (const value of Object.values(parsed as Record<string, unknown>)) {
      nested.push(...findPreviewRowArrays(value, depth + 1));
    }
    return nested;
  }
  return [];
}

function normalizeRow(row: Record<string, unknown>): BulkPricePreviewItem | null {
  const before =
    asNumber(row.beforePrice) ??
    asNumber(row.before_price) ??
    asNumber(row.currentPrice) ??
    asNumber(row.current);
  const after =
    asNumber(row.afterPrice) ??
    asNumber(row.after_price) ??
    asNumber(row.newPrice) ??
    asNumber(row.new);
  if (before == null || after == null) return null;

  const change =
    asNumber(row.priceChangeCents) ??
    asNumber(row.price_change_cents) ??
    after - before;

  return {
    inventoryId: asString(row.inventoryId) ?? asString(row.inventory_id),
    cardName: asString(row.cardName) ?? asString(row.card_name),
    itemName: asString(row.itemName) ?? asString(row.item_name),
    setCode: asString(row.setCode) ?? asString(row.set_code),
    collectorNumber: asString(row.collectorNumber) ?? asString(row.collector_number),
    conditionId: asString(row.conditionId) ?? asString(row.condition_id),
    finishId: asString(row.finishId) ?? asString(row.finish_id),
    languageId: asString(row.languageId) ?? asString(row.language_id),
    beforePriceCents: before,
    afterPriceCents: after,
    changeCents: change,
    marketLowCents: asNumber(row.marketLow) ?? asNumber(row.market_low),
    marketPriceCents: asNumber(row.marketPrice) ?? asNumber(row.market_price),
    status: asString(row.status),
    skipReason: asString(row.skipReason) ?? asString(row.skip_reason),
    anomalyType: asString(row.anomalyType) ?? asString(row.anomaly_type),
    anomalyRatio: asNumber(row.anomalyRatio) ?? asNumber(row.anomaly_ratio),
    confidence: asNumber(row.confidence),
  };
}

/**
 * Extract preview line items from any known Manapool / SyncBase preview wrapper.
 * Accepts the nested `preview` field or the full API response body.
 */
export function extractPreviewItems(previewPayload: unknown): BulkPricePreviewItem[] {
  const candidates = findPreviewRowArrays(previewPayload);
  if (candidates.length === 0) return [];
  // Prefer the largest matching array (full catalog vs a tiny nested sample).
  const rows = candidates.reduce((best, cur) => (cur.length > best.length ? cur : best));
  const items: BulkPricePreviewItem[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const normalized = normalizeRow(row as Record<string, unknown>);
    if (normalized) items.push(normalized);
  }
  return items;
}

export function summarizePreviewItems(items: BulkPricePreviewItem[]): BulkPricePreviewSummary {
  let success = 0;
  let skipped = 0;
  let increases = 0;
  let decreases = 0;
  let unchanged = 0;
  let totalIncreaseCents = 0;
  let totalDecreaseCents = 0;
  let absSum = 0;
  let changedCount = 0;
  let anomalyCount = 0;
  let maxIncrease: BulkPricePreviewItem | null = null;
  let maxDecrease: BulkPricePreviewItem | null = null;

  for (const item of items) {
    const status = (item.status || 'success').toLowerCase();
    const skip = (item.skipReason || 'none').toLowerCase();
    if (status !== 'success' || (skip && skip !== 'none')) {
      skipped += 1;
      continue;
    }
    success += 1;
    if (item.changeCents > 0) {
      increases += 1;
      totalIncreaseCents += item.changeCents;
      if (!maxIncrease || item.changeCents > maxIncrease.changeCents) maxIncrease = item;
    } else if (item.changeCents < 0) {
      decreases += 1;
      totalDecreaseCents += item.changeCents;
      if (!maxDecrease || item.changeCents < maxDecrease.changeCents) maxDecrease = item;
    } else {
      unchanged += 1;
    }
    if (item.changeCents !== 0) {
      absSum += Math.abs(item.changeCents);
      changedCount += 1;
    }
    if (
      item.anomalyType &&
      item.anomalyType.toLowerCase() !== 'normal' &&
      item.anomalyType.toLowerCase() !== 'none'
    ) {
      anomalyCount += 1;
    } else if (item.anomalyRatio != null && item.anomalyRatio >= 1.5) {
      anomalyCount += 1;
    }
  }

  return {
    total: items.length,
    success,
    skipped,
    increases,
    decreases,
    unchanged,
    totalIncreaseCents,
    totalDecreaseCents,
    avgAbsChangeCents: changedCount ? Math.round(absSum / changedCount) : 0,
    maxIncrease,
    maxDecrease,
    anomalyCount,
    items,
  };
}

export function itemLabel(item: BulkPricePreviewItem): string {
  const name = item.cardName || item.itemName || 'Unknown card';
  const set = item.setCode ? ` ${item.setCode}` : '';
  const num = item.collectorNumber ? ` #${item.collectorNumber}` : '';
  const finish = item.finishId && item.finishId !== 'NF' ? ` ${item.finishId}` : '';
  const cond = item.conditionId ? ` ${item.conditionId}` : '';
  return `${name}${set}${num}${finish}${cond}`;
}

/** Largest absolute movers first (success rows with a non-zero Δ). */
export function topMovers(
  items: BulkPricePreviewItem[],
  limit: number | null = null,
): BulkPricePreviewItem[] {
  const sorted = items
    .filter((item) => {
      const status = (item.status || 'success').toLowerCase();
      const skip = (item.skipReason || 'none').toLowerCase();
      return status === 'success' && (!skip || skip === 'none') && item.changeCents !== 0;
    })
    .slice()
    .sort((a, b) => Math.abs(b.changeCents) - Math.abs(a.changeCents));
  if (limit == null || limit <= 0 || limit >= sorted.length) return sorted;
  return sorted.slice(0, limit);
}

export interface BulkPricePreviewMeta {
  totalCount: number | null;
  previewCount: number | null;
  skippedPreviewCount: number | null;
  increases: number | null;
  decreases: number | null;
  unchanged: number | null;
  successfulItems: number | null;
  skippedItems: number | null;
  totalValueChangeCents: number | null;
}

/** Pull Manapool sample/total counters and full-scope statistics when present. */
export function extractPreviewMeta(previewPayload: unknown): BulkPricePreviewMeta {
  const parsed =
    typeof previewPayload === 'string'
      ? (() => {
          try {
            return JSON.parse(previewPayload);
          } catch {
            return null;
          }
        })()
      : previewPayload;

  const empty: BulkPricePreviewMeta = {
    totalCount: null,
    previewCount: null,
    skippedPreviewCount: null,
    increases: null,
    decreases: null,
    unchanged: null,
    successfulItems: null,
    skippedItems: null,
    totalValueChangeCents: null,
  };
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return empty;

  const obj = parsed as Record<string, unknown>;
  const stats =
    obj.statistics && typeof obj.statistics === 'object'
      ? (obj.statistics as Record<string, unknown>)
      : {};
  const counts =
    stats.counts && typeof stats.counts === 'object'
      ? (stats.counts as Record<string, unknown>)
      : {};
  const metrics =
    stats.metrics && typeof stats.metrics === 'object'
      ? (stats.metrics as Record<string, unknown>)
      : {};

  const asCount = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const n = Number(value);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const skipped = obj.skippedPreview;
  const skippedCount = Array.isArray(skipped)
    ? skipped.length
    : asCount(obj.skippedPreviewCount) ?? asCount(obj.skipped_count);

  return {
    totalCount:
      asCount(obj.totalCount) ??
      asCount(obj.total_count) ??
      asCount(counts.totalItems) ??
      asCount(counts.total_items),
    previewCount:
      asCount(obj.previewCount) ??
      asCount(obj.preview_count) ??
      (Array.isArray(obj.preview) ? obj.preview.length : null),
    skippedPreviewCount: skippedCount,
    increases: asCount(counts.increases),
    decreases: asCount(counts.decreases),
    unchanged: asCount(counts.unchanged),
    successfulItems: asCount(counts.successfulItems) ?? asCount(counts.successful_items),
    skippedItems: asCount(counts.skippedItems) ?? asCount(counts.skipped_items),
    totalValueChangeCents:
      asCount(metrics.totalValueChangeCents) ?? asCount(metrics.total_value_change_cents),
  };
}
